import type { CrewlinkClient } from '../supabase-client';
import type { CapturePhotoRow, PhotoClass } from '../database.types';

/**
 * Writer wrappers for `remove_capture_photo` and `reclassify_capture_photo`
 * (migration 0006). M5 / S05-04.
 *
 * The layering rule (CLAUDE.md §8, INV-T02): UI → query hooks → writer
 * wrappers → Postgres RPC. The photo screen never calls `.rpc(...)` and never
 * touches `capture_photo` with `.insert`, `.update` or `.delete` — no such
 * grant exists anyway, which is the point: this boundary mirrors a database
 * that would refuse the shortcut.
 *
 * THE THIRD WRITER — `attach_capture_photo` — IS DELIBERATELY NOT HERE. It is
 * reached through the S05-03 delivery queue's transport, because attaching is
 * one stage of a durable, resumable, idempotent upload rather than a thing a
 * screen does. A second entrance to it would be exactly the alternate upload
 * path S05-04 was told not to build.
 *
 * WHAT CROSSES THIS BOUNDARY: the photo id, and for reclassification the new
 * class. **Not the owner.** Ownership is read from `auth.uid()` inside each
 * function, so acting on someone else's photo is not something the API can
 * express (INV-T23).
 *
 * NEITHER FUNCTION CHANGES CAPTURE OR PROJECT STATUS, and neither is gated on
 * one: a CLOSED Project still allows photo work on an EXISTING Capture (D-029).
 * There is no status argument here to make that possible by accident.
 */

export type PhotoWriteResult =
  | { readonly ok: true; readonly photo: CapturePhotoRow }
  | { readonly ok: false; readonly message: string };

interface RpcFailure {
  readonly message: string;
  readonly code?: string;
}

const failed = (error: RpcFailure): PhotoWriteResult => ({
  ok: false,
  message: describePhotoWriteError(error.message, error.code),
});

/**
 * Remove a photo — NON-DESTRUCTIVELY (PT §9).
 *
 * The row and its Storage objects survive; `removed_at` is set and a
 * `photo_removed` Work Log event is written in the same transaction. Nothing
 * in this wrapper, and nothing in the UI above it, may word this as deletion.
 */
export async function removeCapturePhoto(
  client: CrewlinkClient,
  photoId: string,
): Promise<PhotoWriteResult> {
  try {
    const { data, error } = await client.rpc('remove_capture_photo', {
      p_photo_id: photoId,
    });
    if (error) return failed(error);
    if (!data) return { ok: false, message: 'Could not remove that photo. Please try again.' };
    return { ok: true, photo: data };
  } catch (error) {
    // A dropped connection lands here. It must read as a failure — never as a
    // removal that quietly did not happen.
    return failed({ message: error instanceof Error ? error.message : String(error) });
  }
}

/** Reclassify Before ↔ After. The class is always Brandon's choice (PT §9). */
export async function reclassifyCapturePhoto(
  client: CrewlinkClient,
  photoId: string,
  photoClass: PhotoClass,
): Promise<PhotoWriteResult> {
  try {
    const { data, error } = await client.rpc('reclassify_capture_photo', {
      p_photo_id: photoId,
      p_photo_class: photoClass,
    });
    if (error) return failed(error);
    if (!data) return { ok: false, message: 'Could not change that photo. Please try again.' };
    return { ok: true, photo: data };
  } catch (error) {
    return failed({ message: error instanceof Error ? error.message : String(error) });
  }
}

/**
 * Turn a failure into a sentence Brandon can act on.
 *
 * The database is the authority; this translates its conditions rather than
 * re-implementing the rules client-side where the two could drift.
 *
 * **`P0002` covers "no such photo" AND "not your photo" as ONE case, and the
 * wording keeps them one case** — distinguishing them would leak across users
 * exactly what the shared error code exists to hide (INV-T23).
 *
 * **`CL013` is "that photo is already removed".** It is a real outcome of two
 * devices, or two taps, disagreeing about what is on screen, and the honest
 * answer is that the photo is gone from the active Capture — not that
 * something broke.
 */
export function describePhotoWriteError(message: string, code?: string): string {
  const normalised = `${code ?? ''} ${message}`.toLowerCase();

  if (normalised.includes('cl013') || normalised.includes('photo is removed')) {
    return 'That photo has already been removed from this Capture.';
  }
  if (normalised.includes('cl010')) {
    return 'A photo can only be Before or After.';
  }
  if (normalised.includes('p0002') || normalised.includes('photo not found')) {
    return 'That photo is not available. Reload this Capture and try again.';
  }
  if (normalised.includes('not authenticated') || normalised.includes('42501')) {
    return 'Your session has expired. Sign in again and try once more.';
  }
  if (normalised.includes('failed to fetch') || normalised.includes('network')) {
    return 'Could not reach Crewlink. Check your connection and try again.';
  }
  return 'Could not update that photo. Please try again.';
}
